import { FileText, Download } from "lucide-react";
import { Testimonials } from "@/components/site/Testimonials";
import { PageHero, Container, SectionHead, CtaBand } from "@/components/site/blocks";
import { Reveal } from "@/components/site/motion";
import { useSeo } from "@/components/site/useSeo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQ = [
  { q: "Who is JEM for?", a: "Vocational rehabilitation clients, transition and Pre-ETS students, youth ages 14–22, families and caregivers, VR professionals, educators, Centers for Independent Living, community providers, employers, and college audiences." },
  { q: "What does a typical session look like?", a: "Sessions can include a documentary screening, John's keynote, facilitated discussion, goal setting, Q&A, and a reflection worksheet. The proposed GVRA pilot runs 3–4 hours. Each format is shaped around your audience." },
  { q: "Is the program accessible?", a: "Accessibility is central to JEM. Screenings are presented with captions. Tell us about your group's access needs when you reach out, and we'll plan around them." },
  { q: "How does scheduling work?", a: "Send an inquiry with your organization, audience, and interest. John will follow up to discuss dates, format, and logistics." },
  { q: "Can we show the documentary on our own?", a: "Screening permission is discussed as part of each partnership. The proposed pilot includes limited pilot screening permission. Ask about licensing for other uses." },
  { q: "Does JEM replace our existing services?", a: "No. JEM complements existing individualized services. It does not replace them." },
  { q: "How do we start a partnership?", a: "Use the contact form or reach John directly at jem.partnerships@gmail.com or +1 (678) 310-2630." },
];
const DOWNLOADS = [
  { t: "Program overview", d: "A one-page summary of JEM for decision-makers." },
  { t: "Discussion guide", d: "Questions to help participants reflect on their journey, strengths, barriers, goals, resources, and next steps after the screening.", href: "/downloads/jem-documentary-discussion-guide.pdf" },
];

export default function Resources() {
  useSeo("Resources & FAQ", "JEM program overview, discussion-guide information, and answers about audiences, format, accessibility, scheduling, licensing, and partnerships.");
  return (
    <>
      <PageHero testid="resources-hero" label="Resources & FAQ" lines={["Everything you", <span key="n" className="italic text-[#F3C649]">need to decide.</span>]}
        intro="A short overview, information on the discussion guide, and answers to common questions." />
      <section className="pb-24" data-testid="resources-overview">
        <Container className="grid gap-5 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="h-full rounded-3xl border border-[#D4AF37]/25 bg-[#12141C] p-8 sm:p-10">
              <p className="label">Program overview</p>
              <p className="mt-6 text-lg leading-relaxed text-[#E6E3DA]">JEM is a documentary-based program. It uses John E. Mason's story to encourage resilience, self-advocacy, independence, education, and employment readiness.</p>
              <p className="mt-4 text-[#C8C5BB]">Discussion guides help facilitators connect the film to participants' goals and keep the conversation going after the session.</p>
            </div>
          </Reveal>
          {DOWNLOADS.map((x, i) => (
            <Reveal key={x.t} delay={0.1 * (i + 1)} className="lg:col-span-3">
              {x.href ? (
                <a href={x.href} download className="group flex h-full flex-col justify-between rounded-3xl border border-[#F3C649]/60 bg-[#12141C] p-8 transition-colors duration-500 hover:bg-[#181A24]" data-testid={`download-link-${i}`}>
                  <FileText size={26} aria-hidden="true" className="text-[#F3C649]" />
                  <div className="mt-10">
                    <h2 className="font-display text-2xl font-semibold">Documentary {x.t.toLowerCase()}</h2>
                    <p className="mt-2 text-sm text-[#C8C5BB]">{x.d}</p>
                    <p className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#F3C649] group-hover:underline"><Download size={14} aria-hidden="true" /> Download PDF · 7 pages</p>
                  </div>
                </a>
              ) : (
              <div className="placeholder-box flex h-full flex-col justify-between rounded-3xl p-8" data-testid={`download-placeholder-${i}`}>
                <FileText size={26} aria-hidden="true" className="text-[#F3C649]" />
                <div className="mt-10">
                  <h2 className="font-display text-2xl font-semibold">{x.t}</h2>
                  <p className="mt-2 text-sm text-[#C8C5BB]">{x.d}</p>
                  <p className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F3C649]"><Download size={14} aria-hidden="true" /> PDF coming soon</p>
                </div>
              </div>
              )}
            </Reveal>
          ))}
        </Container>
      </section>
      <section className="bg-[#07080A] py-24 sm:py-32" data-testid="faq-section">
        <Container className="grid gap-14 lg:grid-cols-12">
          <SectionHead className="lg:col-span-4" label="FAQ" title="Common questions." />
          <Reveal className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQ.map((f, i) => (
                <AccordionItem key={f.q} value={`f${i}`} className="border-[#D4AF37]/25">
                  <AccordionTrigger className="py-6 text-left font-display text-2xl font-semibold hover:text-[#F3C649] hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="pb-6 text-base leading-relaxed text-[#C8C5BB]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </Container>
      </section>
      <Testimonials />
      <CtaBand />
    </>
  );
}
